<script>
import { goToLogin } from '@/utils/loginFlow'
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'
import { ElMessage, ElMessageBox } from 'element-plus'
//import { Timer, User, Star } from '@element-plus/icons-vue'
import ImageBar from './components/ImageBar.vue'
import ExamPromoBadge from './components/exam/ExamPromoBadge.vue'
import {
  authListVariant,
  listCacheKey,
  readListCache,
  writeListCache
} from '@/utils/listPageCache'
import {
  isExamFree,
  isExamAcquired,
  isExamChargeable,
  isLimitedTimeFree,
  isLimitedTimePromo,
  getExamPriceText,
  formatExamFeeYuan,
  normalizeExamFromApi,
  examDetailPath,
  examTakePath
} from '@/utils/examPricing'

const defaultThumbnails = [
  'https://coolpeople.com.cn/api/v1/static/images/exams/discHD.jpg',
  'https://coolpeople.com.cn/api/v1/static/images/exams/8valueHD.jpg',
]

export default {
  name: 'ExamesView',
  components: {
    ImageBar,
    ExamPromoBadge
    // Timer,
    // User,
    // Star
  },
  setup() {
    const router = useRouter()
    const examCacheKey = listCacheKey('exam', `${authListVariant()}:originFee`)
    const cached = readListCache(examCacheKey)
    const exams = ref(cached?.exams ? cached.exams.map(normalizeExamFromApi) : [])
    const loading = ref(false)
    const hasMore = ref(cached?.hasMore ?? true)
    const minId = ref(cached?.minId ?? 0)
    const observer = ref(null)
    const loadMoreTrigger = ref(null)
    const getRandomThumbnail = () => {
      const randomIndex = Math.floor(Math.random() * defaultThumbnails.length)
      return defaultThumbnails[randomIndex]
    }

    const setupObserver = () => {
      if (observer.value) {
        observer.value.disconnect()
        observer.value = null
      }

      nextTick(() => {
        if (!loadMoreTrigger.value) {
          console.warn('loadMoreTrigger is not available')
          return
        }

        observer.value = new IntersectionObserver((entries) => {
          const isIntersecting = entries[0].isIntersecting
          if (isIntersecting && !loading.value && hasMore.value) {
            fetchExams(true)
          }
        }, {
          root: null,
          rootMargin: '100px',
          threshold: 0.1
        })

        observer.value.observe(loadMoreTrigger.value)
      })
    }

    const saveExamCache = () => {
      writeListCache(examCacheKey, {
        exams: exams.value,
        hasMore: hasMore.value,
        minId: minId.value
      })
    }

    const fetchExams = async (isLoadMore = false) => {
      if (loading.value) return
      if (isLoadMore && !hasMore.value) return
      
      try {
        loading.value = true
        const token = localStorage.getItem('jwt_token')
        
        // Check if user is logged in
        if (token) {
          // User is logged in, use authenticated endpoint
          const response = await axios.post('/api/v1/exam/list', {
            fromId: isLoadMore ? minId.value : 0
          }, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })

          if (response.data.code === 200) {
            const newExams = (response.data.data.exams || []).map(normalizeExamFromApi)
            
            if (isLoadMore) {
              exams.value = [...exams.value, ...newExams]
            } else {
              exams.value = newExams
            }

            if (newExams.length > 0) {
              minId.value = Math.min(...newExams.map(e => e.id))
              hasMore.value = true
            } else {
              hasMore.value = false
            }

            saveExamCache()
            loading.value = false
            setupObserver()
          } else {
            ElMessage.error(response.data.message || '获取测评列表失败')
          }
        } else {
          // User is not logged in, use guest endpoint
          const response = await axios.post('/api/v1/exam/guest/list', {
            fromId: isLoadMore ? minId.value : 0
          }, {
            headers: {
              'Content-Type': 'application/json'
            }
          })

          if (response.data.code === 200) {
            const newExams = (response.data.data.exams || []).map(normalizeExamFromApi)
            
            if (isLoadMore) {
              exams.value = [...exams.value, ...newExams]
            } else {
              exams.value = newExams
            }

            if (newExams.length > 0) {
              minId.value = Math.min(...newExams.map(e => e.id))
              hasMore.value = true
            } else {
              hasMore.value = false
            }

            saveExamCache()
            loading.value = false
            setupObserver()
          } else {
            ElMessage.error(response.data.message || '获取测评列表失败')
          }
        }
      } catch (error) {
        console.error('Error fetching exams:', error)
        ElMessage.error('获取测评列表失败')
      } finally {
        loading.value = false
      }
    }

    const formatDate = (timestamp) => {
      if (!timestamp) return ''
      const date = new Date(timestamp * 1000)
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    const viewTest = (exam) => {
      router.push(examDetailPath(exam))
    }

    const onCardClick = (exam) => {
      viewTest(exam)
    }

    const startTest = async (exam) => {
      const token = localStorage.getItem('jwt_token')
      if (!token) {
        router.push(examTakePath(exam))
        return
      }

      // Check if exam is already purchased
      if (exam.isAcquired > 0) {
        router.push(examTakePath(exam))
        return
      }
      
      if (isExamChargeable(exam)) {
        // Show confirmation dialog for paid exams
        try {
          await ElMessageBox.confirm(
            `确定要购买此测评吗？费用：¥${formatExamFeeYuan(exam)}`,
            '购买确认',
            {
              confirmButtonText: '确定购买',
              cancelButtonText: '取消',
              type: 'info',
            }
          )
        } catch (error) {
          if (error === 'cancel') {
            return
          }
        }

        try {
          // First check user balance
          const balanceResponse = await axios.get('/api/v1/user/balance', {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })

          if (balanceResponse.data.code === 200 && balanceResponse.data.data && balanceResponse.data.data.length > 0) {
            const userBalance = balanceResponse.data.data[0].balance // Balance in cents
            const examFee = exam.fee // Convert fee to cents
            
            if (userBalance < examFee) {
              ElMessage.error(`余额不足，需要 ¥${(examFee / 100).toFixed(2)}，当前余额 ¥${(userBalance / 100).toFixed(2)}，请先充值`)
              router.push('/recharge')
              return
            }
          }

          // Proceed with purchase
          const response = await axios.post('/api/v1/exam/buy', {
            id: parseInt(exam.id)
          }, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })

          if (response.data.code === 200) {
            ElMessage.success('购买成功，开始测评')
            router.push(examTakePath(exam))
          } else {
            ElMessage.error(response.data.message || '购买失败')
          }
        } catch (error) {
          console.error('Error buying exam:', error)
          if (error.response?.status === 401) {
            ElMessage.error('请先登录')
            goToLogin(router)
          } else if (error.response?.status === 402) {
            ElMessage.error('余额不足，请先充值')
            router.push('/recharge')
          } else {
            ElMessage.error('购买失败，请重试')
          }
        }
      } else {
        // Free exam, start directly
        router.push(examTakePath(exam))
      }
    }

    onMounted(() => {
      fetchExams()
    })

    onUnmounted(() => {
      if (observer.value) {
        observer.value.disconnect()
        observer.value = null
      }
    })

    return {
      exams,
      loading,
      hasMore,
      loadMoreTrigger,
      formatDate,
      viewTest,
      onCardClick,
      isExamFree,
      isExamAcquired,
      isExamChargeable,
      isLimitedTimeFree,
      isLimitedTimePromo,
      getExamPriceText,
      formatExamFeeYuan,
      startTest,
      getRandomThumbnail
    }
  }
}
</script>

<template>
     <ImageBar title="测评列表"  height="80px"/>
  <div class="exames-container nav-content-inset">
    <div class="exames-content">
      <div class="masonry-grid" v-loading="loading && exams.length === 0">
        <div v-for="exam in exams" :key="exam.id" class="masonry-item">
          <el-card
            class="exam-card exam-card-clickable"
            :class="{
              'card-limited-free': isLimitedTimeFree(exam),
              'card-limited-promo': isLimitedTimePromo(exam),
              'card-free': isExamFree(exam) && !isLimitedTimeFree(exam) && !isLimitedTimePromo(exam),
              'card-bought': isExamAcquired(exam) && !isExamFree(exam),
              'card-not-bought': isExamChargeable(exam) && !isExamAcquired(exam)
            }"
            shadow="hover"
            @click="onCardClick(exam)"
          >
            <ExamPromoBadge :exam="exam" variant="card-ribbon" />
            <div class="card-header">
              <h3>{{ exam.examName }}</h3>
            </div>
            <div class="card-content">
              <div class="thumbnail-container">
                <img :src="exam.thumb || getRandomThumbnail()" class="thumbnail" alt="测评封面" />
                <span
                  v-if="isExamAcquired(exam) && !isExamFree(exam)"
                  class="img-badge badge-bought"
                >已购买</span>
              </div>
              <p class="profile">{{ exam.profile }}</p>
              <!-- <p class="description">{{ exam.description }}</p> -->
              <p class="meta">
                <span class="date">创建时间：{{ formatDate(exam.createdAt) }}</span>
              </p>
            </div>
            <div class="card-actions" @click.stop>
              <div class="fee-info" v-if="exam.fee !== undefined && exam.fee !== null">
                <span class="fee-text" :class="{
                  'fee-limited-free': isLimitedTimeFree(exam),
                  'fee-limited-promo': isLimitedTimePromo(exam),
                  'fee-free': isExamFree(exam) && !isLimitedTimeFree(exam) && !isLimitedTimePromo(exam),
                  'fee-bought': isExamAcquired(exam) && !isExamFree(exam),
                  'fee-not-bought': isExamChargeable(exam) && !isExamAcquired(exam)
                }">
                  {{ getExamPriceText(exam) }}
                </span>
              </div>
              <el-button type="primary" size="small" @click.stop="startTest(exam)">
                开始测试
              </el-button>
            </div>
          </el-card>
        </div>
      </div>

      <div v-if="loading && exams.length > 0" class="loading-more">
        <el-skeleton :rows="1" animated />
      </div>
      <div v-else-if="!hasMore" class="no-more">
        没有更多测评了
      </div>
      <div v-else ref="loadMoreTrigger" class="load-more-trigger"></div>
    </div>

  </div>
</template>

<style scoped>
.exames-container {
  max-width: 1800px;
  margin: 0 auto;
  padding: 20px;
}

.exames-content {
  background-color: #fff;
}

.masonry-grid {
  column-count: 4;
  column-gap: 16px;
  padding: 0;
}

.masonry-item {
  break-inside: avoid;
  margin-bottom: 16px;
  display: block;
}

.exam-card {
  position: relative;
  height: auto;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
  border-radius: 12px;
  overflow: hidden;
}

.card-limited-free {
  background: linear-gradient(135deg, #fff9f5 0%, #fff1f0 100%);
  border: 2px solid rgba(245, 108, 108, 0.35);
  box-shadow: 0 4px 20px rgba(245, 108, 108, 0.12);
}

.card-limited-free:hover {
  border-color: rgba(245, 108, 108, 0.55);
  box-shadow: 0 10px 28px rgba(245, 108, 108, 0.2);
  transform: translateY(-2px);
}

.card-limited-promo {
  background: linear-gradient(135deg, #fffbf5 0%, #fff7e8 100%);
  border: 2px solid rgba(230, 162, 60, 0.35);
}

.card-free {
  background: linear-gradient(135deg, #fafbfc 0%, #f5f7fa 100%);
  border: 1px solid rgba(103, 194, 58, 0.15);
}

.card-free:hover {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8f4fd 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(103, 194, 58, 0.08);
}

.card-bought {
  background: linear-gradient(135deg, #fafbfc 0%, #f5f7fa 100%);
  border: 1px solid rgba(64, 158, 255, 0.15);
}

.card-bought:hover {
  background: linear-gradient(135deg, #f5f7fa 0%, #e8f4fd 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(64, 158, 255, 0.08);
}

.card-not-bought {
  background: linear-gradient(135deg, #fefcf8 0%, #faf6f0 100%);
  border: 1px solid rgba(230, 162, 60, 0.15);
}

.card-not-bought:hover {
  background: linear-gradient(135deg, #faf6f0 0%, #f5f0e6 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(230, 162, 60, 0.08);
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--el-border-color-light);
}

.card-header h3 {
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: 18px;
  color: var(--el-text-color-primary);
}

.card-content {
  flex: 1;
  padding: 15px 0;
  display: flex;
  flex-direction: column;
}

.exam-card-clickable {
  cursor: pointer;
}

.exam-card-clickable :deep(.el-card__body) {
  height: 100%;
}

.thumbnail-container {
  position: relative;
  width: 100%;
  margin-bottom: 15px;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.img-badge.badge-bought {
  position: absolute;
  z-index: 2;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  color: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  letter-spacing: 0.5px;
}

.badge-bought {
  top: 0;
  right: 0;
  background: linear-gradient(135deg, #409eff 0%, #337ecc 100%);
  border-bottom-left-radius: 10px;
  border-radius: 0 0 0 10px;
}

.thumbnail {
  width: 100%;
  height: 180px;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.thumbnail:hover {
  transform: scale(1.02);
}

.profile {
  font-size: 14px;
  color: var(--el-text-color-primary);
  margin-bottom: 10px;
  line-height: 1.4;
  flex: 1;
}

.description {
  color: var(--el-text-color-regular);
  margin-bottom: 10px;
  line-height: 1.5;
}

.meta {
  color: var(--el-text-color-secondary);
  font-size: 14px;
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.fee {
  color: var(--el-color-primary);
  font-weight: 500;
}

.card-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 15px;
  border-top: 1px solid var(--el-border-color-light);
}

.fee-info {
  display: flex;
  align-items: center;
}

.fee-text {
  font-weight: 500;
  font-size: 16px;
  padding: 4px 8px;
  border-radius: 4px;
}

.fee-limited-free {
  color: #f56c6c;
  background-color: #fef0f0;
  border: 1px solid #fde2e2;
}

.fee-limited-promo {
  color: #e6a23c;
  background-color: #fefcf8;
  border: 1px solid #f5e6c8;
}

.fee-free {
  color: #67c23a;
  background-color: #f8fafc;
  border: 1px solid #e8f4fd;
}

.fee-bought {
  color: #67c23a;
  background-color: #f0f9ff;
  border: 1px solid #b3d8ff;
}

.fee-not-bought {
  color: #e6a23c;
  background-color: #fefcf8;
  border: 1px solid #f5f0e6;
}

.loading-more {
  padding: 20px;
  text-align: center;
}

.no-more {
  text-align: center;
  padding: 20px;
  color: var(--el-text-color-secondary);
}

.load-more-trigger {
  height: 1px;
  width: 100%;
}

@media (max-width: 1200px) {
  .masonry-grid {
    column-count: 3;
  }
}

@media (max-width: 900px) {
  .masonry-grid {
    column-count: 2;
  }
}

@media (max-width: 768px) {
  .exames-container {
    padding: 0;
  }

  .exames-content {
    padding: 0;
  }

  .masonry-grid {
    column-count: 2;
    column-gap: 8px;
  }

  .masonry-item {
    margin-bottom: 8px;
  }

  .thumbnail {
    height: 150px;
  }
}

@media (max-width: 480px) {
  .masonry-grid {
    column-count: 1;
    column-gap: 12px;
  }
}
</style> 